import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  ThemeProvider,
  Toolbar,
  Typography,
  useScrollTrigger
} from "@mui/material";
import {
  CloseRounded,
  DashboardRounded,
  LogoutRounded,
  MenuRounded,
  PersonRounded
} from "@mui/icons-material";
import { NotificationBar } from "app/components/NotificationBar";
import { NotificationProvider } from "app/contexts/NotificationContext";
import { useGetAdmin } from "app/hooks/admin/useGetAdmin";
import useGetCandidate from "app/hooks/candidate/useGetCandidate";
import { useGetCompany } from "app/hooks/company/useGetCompany";
import useAuth from "app/hooks/useAuth";
import { AppRoutes } from "app/utils/global_constants";
import { convertToBase64, getInitialsOfName } from "app/utils/utils";
import nextHireLogo from "../../assets/logo/nexthire-transparent.webp";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import landingTheme, { LANDING_LAYOUT } from "app/views/landing_page/theme";

const NAV_ITEMS = [
  { label: "Features", href: "/#features" },
  { label: "For Employers", href: "#employers" },
  { label: "For Candidates", href: "#candidates" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Resources", href: "/#resources" },
  {
    label: "About Us",
    href: "https://www.reyan.co.in/about.php",
    external: true
  }
];

function Brand({ onClick }) {
  return (
    <ButtonBase
      aria-label="NextHire home"
      onClick={onClick}
      sx={{ borderRadius: 1.5, flexShrink: 0, p: 0.25 }}
    >
      <Box
        component="img"
        src={nextHireLogo}
        alt="NextHire"
        sx={{
          display: "block",
          height: "auto",
          width: 80
        }}
      />
    </ButtonBase>
  );
}

function AccountActions({ onNavigate }) {
  const { logout, user } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const userId = user?.id;
  const role = user?.role;
  const { data: candidate } = useGetCandidate(role === "candidate" ? userId : null);
  const { data: company } = useGetCompany({ id: role === "company" ? userId : null });
  const { data: admin } = useGetAdmin({ id: role === "SA" ? userId : null });

  const profilePhoto =
    role === "candidate"
      ? candidate?.jp_photo?.data
      : role === "company"
        ? company?.jp_logo?.data
        : admin?.jp_photo?.data;
  const avatarSrc = profilePhoto
    ? `data:image/jpeg;base64,${convertToBase64(profilePhoto)}`
    : undefined;
  const initials = role === "SA" ? "HR" : getInitialsOfName(user?.name);

  const closeMenu = () => setMenuAnchor(null);
  const navigateFromMenu = (route) => {
    closeMenu();
    onNavigate(route);
  };
  const handleLogout = () => {
    closeMenu();
    logout(role);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={{ xs: 0.25, sm: 0.75 }}>
      <Box
        sx={{
          "& > .MuiIconButton-root": { height: 42, width: 42 },
          "& .MuiBadge-badge": {
            bgcolor: "#1976d2",
            color: "common.white",
            fontSize: 12,
            fontWeight: 700,
            height: 21,
            minWidth: 21
          }
        }}
      >
        <NotificationProvider>
          <NotificationBar />
        </NotificationProvider>
      </Box>

      <ButtonBase
        aria-label="Open profile menu"
        aria-controls={menuAnchor ? "header-profile-menu" : undefined}
        aria-expanded={menuAnchor ? "true" : undefined}
        aria-haspopup="menu"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        sx={{
          alignItems: "center",
          borderRadius: 3,
          display: "flex",
          gap: { xs: 0, md: 1.25 },
          p: 0.5,
          transition: "background-color 160ms ease",
          "&:hover": { bgcolor: "primary.light" }
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "text.primary",
            display: { xs: "none", md: "block" },
            fontSize: 16,
            maxWidth: 160,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          Hi <Box component="strong" sx={{ fontWeight: 700 }}>{user?.name}</Box>
        </Typography>
        <Avatar
          src={avatarSrc}
          alt={user?.name || "Profile"}
          sx={{
            bgcolor: "primary.main",
            border: "3px solid",
            borderColor: "background.paper",
            boxShadow: "0 0 0 1px rgba(69, 30, 187, 0.14)",
            color: "common.white",
            fontSize: 15,
            fontWeight: 700,
            height: { xs: 38, sm: 44 },
            width: { xs: 38, sm: 44 }
          }}
        >
          {initials}
        </Avatar>
      </ButtonBase>

      <Menu
        id="header-profile-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{
          paper: {
            sx: {
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: "0 16px 42px rgba(55, 36, 111, 0.16)",
              mt: 1,
              minWidth: 190,
              p: 0.75
            }
          }
        }}
      >
        <MenuItem onClick={() => navigateFromMenu(AppRoutes.ROUTE_DASHBOARD)}>
          <ListItemIcon>
            <DashboardRounded fontSize="small" />
          </ListItemIcon>
          Dashboard
        </MenuItem>
        <MenuItem onClick={() => navigateFromMenu(AppRoutes.ROUTE_PROFILE)}>
          <ListItemIcon>
            <PersonRounded fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider sx={{ my: 0.75 }} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Stack>
  );
}

function HeaderContent({ position }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const elevated = useScrollTrigger({ disableHysteresis: true, threshold: 16 });

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const goTo = (route) => {
    closeMobileMenu();
    navigate(route);
  };

  const renderGuestActions = () => (
    <>
      <Button
        variant="outlined"
        onClick={() => goTo(AppRoutes.ROUTE_LOGIN_COMPANY)}
        sx={{ whiteSpace: "nowrap" }}
      >
        Company Login
      </Button>
      <Button
        variant="contained"
        onClick={() => goTo(AppRoutes.ROUTE_LOGIN_CANDIDATE)}
        sx={{ whiteSpace: "nowrap" }}
      >
        Candidate Login
      </Button>
    </>
  );

  return (
    <>
      <AppBar
        color="transparent"
        elevation={0}
        position={position}
        sx={{
          backdropFilter: "blur(18px)",
          backgroundColor: elevated ? "rgba(251, 248, 255, 0.96)" : "rgba(251, 248, 255, 0.84)",
          borderBottom: "1px solid",
          borderColor: elevated ? "divider" : "rgba(201, 196, 215, 0.45)",
          boxShadow: elevated ? "0 10px 30px rgba(55, 36, 111, 0.08)" : "none",
          transition: "background-color 180ms ease, box-shadow 180ms ease"
        }}
      >
        <Container
          maxWidth={false}
          sx={{ maxWidth: LANDING_LAYOUT.maxWidth, px: { xs: 2, sm: 3 } }}
        >
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              minHeight: { xs: 70, md: 76 }
            }}
          >
            <Box sx={{ display: "flex", flexShrink: 0 }}>
              <Brand onClick={() => navigate("/")} />
            </Box>

            <Stack
              component="nav"
              aria-label="Primary navigation"
              direction="row"
              spacing={0}
              sx={{
                alignItems: "center",
                display: { xs: "none", lg: "flex" },
                flex: 1,
                justifyContent: "space-evenly"
              }}
            >
              {NAV_ITEMS.map((item, index) => (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={closeMobileMenu}
                  rel={item.external ? "noreferrer" : undefined}
                  target={item.external ? "_blank" : undefined}
                  sx={{
                    alignItems: "center",
                    color: index === 0 ? "primary.main" : "text.secondary",
                    display: "inline-flex",
                    fontSize: 15,
                    fontWeight: 700,
                    minHeight: 42,
                    whiteSpace: "nowrap",
                    "&:hover": { color: "primary.main" }
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>

            {user ? (
              <Box sx={{ flexShrink: 0, ml: { xs: "auto", lg: 0 } }}>
                <AccountActions onNavigate={goTo} />
              </Box>
            ) : (
              <Stack
                direction="row"
                spacing={1.25}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexShrink: 0,
                  ml: { xs: "auto", lg: 0 }
                }}
              >
                {renderGuestActions()}
              </Stack>
            )}

            <IconButton
              aria-label="Open navigation menu"
              color="primary"
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                display: { xs: "inline-flex", lg: "none" },
                height: 44,
                ml: { xs: "auto", sm: 1.5 },
                width: 44
              }}
            >
              <MenuRounded />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        PaperProps={{
          sx: {
            bgcolor: "background.default",
            p: 2.5,
            width: { xs: "min(88vw, 360px)", sm: 360 }
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Brand onClick={() => goTo("/")} />
          <IconButton aria-label="Close navigation menu" onClick={closeMobileMenu}>
            <CloseRounded />
          </IconButton>
        </Stack>

        <Stack component="nav" aria-label="Mobile navigation" spacing={0.5}>
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              onClick={closeMobileMenu}
              rel={item.external ? "noreferrer" : undefined}
              target={item.external ? "_blank" : undefined}
              sx={{
                borderRadius: 1.5,
                color: "text.primary",
                fontSize: 16,
                fontWeight: 700,
                px: 1.5,
                py: 1.25,
                "&:hover": { bgcolor: "primary.light", color: "primary.main" }
              }}
            >
              {item.label}
            </Link>
          ))}
        </Stack>

        {!user && (
          <Stack spacing={1.25} sx={{ mt: "auto", pt: 4 }}>
            {renderGuestActions()}
          </Stack>
        )}
      </Drawer>
    </>
  );
}

export default function Header({ position = "fixed" }) {
  return (
    <ThemeProvider theme={landingTheme}>
      <HeaderContent position={position} />
    </ThemeProvider>
  );
}
